import { Button, Drawer, Flex, Text, useMantineTheme } from '@mantine/core';
import { Select } from 'react-hook-form-mantine';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import React, { useEffect, useMemo, useState } from 'react';
import { generatePath, useNavigate } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';

import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { ManagePivDrawerStyles } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/ManagePivDrawer/ManagePivDrawer.styles.tsx';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import {
	DEAL_ROUTES,
	PEOPLE_ROUTES,
	ROUTES,
} from '@dealstack/frontend/app/pages/Routing/routes.ts';
import { ManagePivFormValues } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/components/InvestmentDetailsSidebar/components/ManagePivDrawer/ManagePivDrawer.types.ts';
import { TwoSideToggle } from '@dealstack/frontend/shared/components/TwoSideToggle/TwoSideToggle.tsx';
import { setInvestmentDetailsPiv } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2.tsx';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer.ts';
import { GetPersonByIdRes, InvestmentDetailsPiv } from '@dealstack/shared/lib/generated';

import { ConfirmationModalV3 } from '@dealstack/frontend/shared/components/ConfirmationModal/ConfirmationModalV3.tsx';
import { InfoBox } from '@dealstack/frontend/shared/components/InfoBox/InfoBox.tsx';

interface Props {
	opened: boolean;
	onClose: () => void;
	onSaved: () => void;
	personId: string | null;
	piv: InvestmentDetailsPiv | null;
	entities: GetPersonByIdRes[];
	hasSelectedBankAccount: boolean;
}

const schema = z
	.object({
		pivId: z.string(),
		withPiv: z.boolean(),
	})
	.refine((values) => !values.withPiv || values.pivId.length > 0, {
		path: ['pivId'],
		message: 'Select an investment vehicle',
	});

export const ManagePivDrawer = ({
	opened,
	onClose,
	onSaved,
	personId,
	piv,
	entities,
	hasSelectedBankAccount,
}: Props) => {
	const tMipManage = useTranslationMipManage();
	const navigate = useNavigate();
	const { dealId, mipId, investmentId } = useRequiredParams('dealId', 'mipId', 'investmentId');
	const mantineTheme = useMantineTheme();
	const [pendingSubmit, setPendingSubmit] = useState<ManagePivFormValues | null>(null);

	const setPivMutation = useMutation({
		mutationFn: setInvestmentDetailsPiv,
	});

	const pivOptions = useMemo(
		() =>
			(entities ?? []).map((entity) => ({
				value: entity.id,
				label: entity.displayName,
			})),
		[entities]
	);

	const form = useForm<ManagePivFormValues>({
		resolver: zodResolver(schema),
		defaultValues: {
			pivId: piv?.id ?? '',
			withPiv: !!piv,
		},
	});

	const pivToggled = form.watch('withPiv');
	const selectedPivId = form.watch('pivId');

	useEffect(() => {
		if (opened) {
			form.reset({
				pivId: piv?.id ?? '',
				withPiv: !!piv,
			});
		}
	}, [opened, piv, form]);

	const resolveNextPivPersonId = (values: ManagePivFormValues): string | null =>
		values.withPiv && values.pivId ? values.pivId : null;

	const executePivChange = (values: ManagePivFormValues) => {
		setPivMutation.mutate(
			{
				mipId,
				investmentId,
				payload: { pivPersonId: resolveNextPivPersonId(values) },
			},
			{
				onSuccess: () => {
					onSaved();
					showToastV2({
						title: tMipManage('manage_investment_piv_drawer.update_success'),
						state: 'success',
					});
					onClose();
				},
				onError: (error) => {
					showToastV2({
						title: tMipManage('manage_investment_piv_drawer.update_error'),
						message: isStandardBackendError(error)
							? `error code ${error.error?.code}`
							: undefined,
						state: 'error',
					});
				},
			}
		);
	};

	const onSubmit = (values: ManagePivFormValues) => {
		const isPivChanging = resolveNextPivPersonId(values) !== (piv?.id ?? null);

		if (isPivChanging && hasSelectedBankAccount) {
			setPendingSubmit(values);
			return;
		}

		executePivChange(values);
	};

	const togglePiv = (toggled: boolean) => {
		form.setValue('withPiv', toggled);

		if (!toggled) {
			form.setValue('pivId', '');
		}
	};

	const selectedEntity = useMemo(
		() => (entities ?? []).find((entity) => entity.id === selectedPivId) ?? null,
		[entities, selectedPivId]
	);
	const currentSelected = piv?.id === selectedEntity?.id;

	const legalRepresentative = selectedEntity?.representative ?? null;
	const registrationNumber =
		selectedEntity?.registrationDetails?.corporateRegistrationNumber ?? null;
	const addressCountry = selectedEntity?.addresses?.[0]?.country ?? null;
	const showPivBox = pivToggled && !!selectedEntity;

	return (
		<>
			<Drawer
				opened={opened}
				onClose={onClose}
				position="right"
				size="484px"
				title={tMipManage('manage_investment_piv_drawer.title')}
				transitionProps={drawerTransitionProps}
				trapFocus={false}
			>
				<ManagePivDrawerStyles>
					<FormProvider {...form}>
						<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
							<Flex direction="column" h="100%">
								<Text fw={600} c="dark.8" mb="0.5rem">
									{tMipManage('manage_investment_piv_drawer.subtitle')}
								</Text>

								<TwoSideToggle
									leftLabel={tMipManage(
										'manage_investment_piv_drawer.toggle.left_label'
									)}
									rightLabel={tMipManage(
										'manage_investment_piv_drawer.toggle.right_label'
									)}
									toggled={pivToggled}
									onToggle={togglePiv}
								/>

								<Select
									name="pivId"
									label={tMipManage('manage_investment_piv_drawer.select.label')}
									placeholder={tMipManage(
										'manage_investment_piv_drawer.select.placeholder'
									)}
									data={pivOptions}
									searchable
									size="md"
									disabled={!pivToggled}
									styles={{
										root: {
											margin: '1rem 0',
											opacity: pivToggled ? 1 : 0.5,
										},
									}}
								/>

								{!pivOptions?.length && (
									<div className="piv-warning">
										<InfoBox
											iconVariant="warning"
											title={tMipManage(
												'manage_investment_piv_drawer.piv_options.alert.title'
											)}
											description={tMipManage(
												'manage_investment_piv_drawer.piv_options.alert.description'
											)}
										/>
									</div>
								)}

								<Flex
									className={`piv-box ${showPivBox ? '' : 'piv-box-hidden'}`}
									justify="space-between"
									align="flex-start"
								>
									<Flex direction="column" gap="0.25rem">
										{selectedEntity?.displayName && (
											<Text fw={600} c="dark.9">
												{selectedEntity.displayName}
											</Text>
										)}

										{registrationNumber && (
											<Text fw={400} c="dark.9">
												{tMipManage(
													'manage_investment_piv_drawer.box.registration',
													{
														registrationNumber,
													}
												)}
											</Text>
										)}

										{addressCountry && (
											<Text fw={400} c="dark.9">
												{tMipManage(
													'manage_investment_piv_drawer.box.address',
													{
														country: addressCountry,
													}
												)}
											</Text>
										)}

										{legalRepresentative && (
											<Text fw={400} c="dark.9">
												{tMipManage(
													'manage_investment_piv_drawer.box.legal_representative',
													{
														name: legalRepresentative.displayName,
													}
												)}
											</Text>
										)}
									</Flex>

									{currentSelected && (
										<Text className="current-pill" fz="sm" c="dark.4">
											{tMipManage(
												'manage_investment_piv_drawer.current_pill'
											)}
										</Text>
									)}
								</Flex>

								<Flex justify="flex-start" mt="1rem">
									<Button
										fw={600}
										variant="subtle"
										onClick={() => {
											if (!personId) {
												return;
											}
											navigate({
												pathname: generatePath(
													`${ROUTES.DEAL}${DEAL_ROUTES.PEOPLE}${PEOPLE_ROUTES.PERSON_DETAILS}`,
													{
														dealId,
														personId,
													}
												),
											});
										}}
									>
										<Text mr="1rem" color="blue.6">
											{tMipManage('manage_investment_piv_drawer.view_button')}
										</Text>

										<Icon
											variant="arrow-up-right-2"
											size="md"
											color={mantineTheme.colors.blue[6]}
											fillStroke={true}
										/>
									</Button>
								</Flex>

								<Text c="dark.9" mt="0.25rem">
									{tMipManage('manage_investment_piv_drawer.more_label')}
								</Text>

								<Flex direction="column" mt="auto" gap="1rem">
									<Button
										type="submit"
										fw={600}
										fullWidth
										loading={setPivMutation.isPending}
									>
										{tMipManage('manage_investment_piv_drawer.cta.save')}
									</Button>

									<Button
										variant="subtle"
										c="dark.8"
										fullWidth
										loading={setPivMutation.isPending}
										onClick={onClose}
									>
										{tMipManage('manage_investment_piv_drawer.cta.cancel')}
									</Button>
								</Flex>
							</Flex>
						</form>
					</FormProvider>
				</ManagePivDrawerStyles>
			</Drawer>

			{pendingSubmit && (
				<ConfirmationModalV3
					title={tMipManage('manage_investment_piv_drawer.bank_account_clear.title')}
					description={tMipManage(
						'manage_investment_piv_drawer.bank_account_clear.description'
					)}
					state="warning"
				>
					<Flex gap="xs">
						<Button
							size="md"
							variant="secondary"
							onClick={() => setPendingSubmit(null)}
						>
							{tMipManage('manage_investment_piv_drawer.bank_account_clear.back')}
						</Button>

						<Button
							size="md"
							variant="subtle"
							onClick={() => {
								const values = pendingSubmit;
								setPendingSubmit(null);
								executePivChange(values);
							}}
						>
							{tMipManage('manage_investment_piv_drawer.bank_account_clear.confirm')}
						</Button>
					</Flex>
				</ConfirmationModalV3>
			)}
		</>
	);
};
